import { generatePath } from "react-router-dom";

import { Avatar } from "@mantine/core";
import { type ColumnDef } from "@tanstack/react-table";

import { AbsoluteRoute } from "../../../core/routes/absoluteRoutes";
import { RouteEntities } from "../../../core/routes/relativeRoutes";
import {
    LensPlusSubscriptionsEarned,
    MonthPayout,
    TotalLensPlusAudience,
} from "../../../lens-creator-payout/components/messages.generated";
import { formatMilliUsd } from "../../../lens-creator-payout/utils/formatMilliUsd";
import { formatNullableNumber } from "../../../lens-stats/formatters/formatNumbers";
import { InsightsRoundButton } from "../../../lenses/components/list/row/InsightsRoundButton";
import { LinkStyled, truncate } from "../../../shared/styles";
import { type LensPlusTableRow } from "../../models/LensPlusTableRow";
import { ColumnIcon, ColumnName } from "../top-performer/messages.generated";

import { LensPlusPayoutChip } from "./LensPlusPayoutChip";
import { MonthScore } from "./messages.generated";

export const formatLensPlusSignups = (value: number | null | undefined): string =>
    value !== null && value !== undefined && value > 0 && value < 10 ? "<10" : formatNullableNumber(value);

export const lensPlusLensColumns = (
    month: string,
    minimum: number,
    search: string,
    allTime = false,
): ColumnDef<LensPlusTableRow>[] => [
    {
        id: "icon",
        header: () => <ColumnIcon />,
        cell: ({ row }) => <Avatar src={row.original.lens?.iconUrl} size={36} radius="md" />,
        size: 60,
        enableSorting: false,
    },
    {
        id: "name",
        accessorFn: (row) => row.lens?.name ?? row.unlockableId,
        header: () => <ColumnName />,
        cell: ({ row: { original } }) =>
            original.lens ? (
                <LinkStyled
                    to={generatePath(AbsoluteRoute.LENS, { lensId: original.lens.id })}
                    className={`paragraph-p300 ${truncate}`}
                >
                    {original.lens.name}
                </LinkStyled>
            ) : (
                String(original.unlockableId)
            ),
        size: 240,
        enableSorting: true,
        sortUndefined: "last",
    },
    {
        id: "users",
        accessorFn: (row) => row.totals?.subscriberActionsUu ?? undefined,
        enableGlobalFilter: false,
        header: () => <TotalLensPlusAudience />,
        cell: ({ row }) => formatNullableNumber(row.original.totals?.subscriberActionsUu),
        size: 160,
        enableSorting: true,
        sortUndefined: "last",
    },
    {
        id: "signups",
        accessorFn: (row) => {
            const value = row.totals?.subscribeCount;
            return typeof value === "number" && value > 0 && value < 10 ? 1 : value ?? undefined;
        },
        enableGlobalFilter: false,
        header: () => <LensPlusSubscriptionsEarned />,
        cell: ({ row }) => formatLensPlusSignups(row.original.totals?.subscribeCount),
        size: 160,
        enableSorting: true,
        sortUndefined: "last",
    },
    {
        id: "score",
        accessorFn: (row) => row.totals?.lensScore ?? undefined,
        enableGlobalFilter: false,
        header: () => <MonthScore month={month} />,
        cell: ({ row }) => <strong>{formatNullableNumber(row.original.totals?.lensScore)}</strong>,
        size: 160,
        enableSorting: true,
        sortUndefined: "last",
    },
    {
        id: "payout",
        enableGlobalFilter: false,
        header: () => <MonthPayout month={month} />,
        cell: ({ row: { original } }) =>
            allTime ? (
                original.paidAmountMilliUsd === undefined ? (
                    "-"
                ) : (
                    formatMilliUsd(original.paidAmountMilliUsd)
                )
            ) : original.window ? (
                <LensPlusPayoutChip window={original.window} minimum={minimum} />
            ) : (
                "-"
            ),
        size: 160,
        enableSorting: false,
    },
    {
        id: "actions",
        header: () => null,
        cell: ({ row: { original } }) =>
            original.lens ? (
                <InsightsRoundButton
                    lensId={original.lens.id}
                    orgId={original.lens.organizationId}
                    lensFolderId={original.lens.lensAccountId?.lensSpaceId ?? ""}
                    insightsTab={RouteEntities.Monetization}
                    search={search}
                    isDisabled={false}
                />
            ) : null,
        size: 60,
        enableSorting: false,
    },
];
